import { num } from '@lib/format';
import { Button } from './Button';
import { Icon } from './Icon';

export function Pager({ total, offset, limit, onChange, noun = 'data' }: { total: number; offset: number; limit: number; onChange: (offset: number) => void; noun?: string }) {
  if (total <= limit && offset === 0) return total ? <div className="pager"><span>{total} {noun}</span></div> : null;
  const end = Math.min(total, offset + limit);
  return (
    <nav className="pager" aria-label="Halaman">
      <span>
        {offset + 1}–{end} dari {total} {noun}
      </span>
      <div className="pager-btns">
        <Button size="sm" icon="back" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>
          Sebelumnya
        </Button>
        <Button size="sm" disabled={end >= total} onClick={() => onChange(offset + limit)}>
          Berikutnya
          <Icon name="next" size={16} />
        </Button>
      </div>
    </nav>
  );
}

/**
 * Pages of an offset list as the API describes them (`OffsetMeta`): "26–50 dari 120 lokasi", Sebelumnya ·
 * Berikutnya. One page only: just the count. Same look as `Pager`.
 */
export function OffsetPager({ meta, count, onPage, noun = 'data' }: { meta: { page: number; limit: number; total: number; hasNext: boolean; hasPrev: boolean }; count: number; onPage: (page: number) => void; noun?: string }) {
  if (!meta.hasNext && !meta.hasPrev) return meta.total ? <div className="pager"><span>{num(meta.total)} {noun}</span></div> : null;
  const start = (meta.page - 1) * meta.limit + 1;
  return (
    <nav className="pager" aria-label="Halaman">
      <span aria-live="polite">
        {count ? `${num(start)}–${num(start + count - 1)} dari ${num(meta.total)} ${noun}` : `Tidak ada ${noun} di halaman ini`}
      </span>
      <div className="pager-btns">
        <Button size="sm" icon="back" disabled={!meta.hasPrev} onClick={() => onPage(meta.page - 1)}>
          Sebelumnya
        </Button>
        <Button size="sm" disabled={!meta.hasNext} onClick={() => onPage(meta.page + 1)}>
          Berikutnya
          <Icon name="next" size={16} />
        </Button>
      </div>
    </nav>
  );
}

/**
 * Pages over a cursor (no grand total needed): "Menampilkan 51–100", optionally "dari N" / "dari lebih dari N",
 * Terbaru (only past the newest page) · Sebelumnya · Berikutnya (disabled at either end) and, given `sizes`, the
 * page size. Same look as `Pager`. A list not ordered by time names its first page with `firstLabel`.
 */
export function CursorPager({ start, count, total, hasPrev, hasNext, onFirst, onPrev, onNext, size, sizes, onSize, noun = 'data', firstLabel = 'Terbaru' }: {
  start: number;
  count: number;
  total?: { count: number; exact: boolean } | null;
  hasPrev: boolean;
  hasNext: boolean;
  onFirst: () => void;
  onPrev: () => void;
  onNext: () => void;
  size: number;
  /** page sizes to choose from; without them (or with one) there is no size select */
  sizes?: readonly number[];
  onSize?: (n: number) => void;
  noun?: string;
  /** the button back to the first page: "Terbaru" for a list newest first, e.g. "Halaman pertama" for a ranking */
  firstLabel?: string;
}) {
  const end = start + Math.max(count, 1) - 1;
  const of = total ? ` dari ${total.exact ? '' : 'lebih dari '}${num(total.count)}` : '';
  return (
    <nav className="pager pager-cursor" aria-label="Halaman">
      <span aria-live="polite">
        {count ? `Menampilkan ${num(start)}–${num(end)}${of} ${noun}` : `Tidak ada ${noun} di halaman ini`}
      </span>
      <div className="pager-btns">
        {sizes && sizes.length > 1 && onSize && (
          <label className="pager-size">
            <span>Per halaman</span>
            <select value={size} onChange={(e) => onSize(Number(e.target.value))}>
              {sizes.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        )}
        {/* on the newest page already: nothing to go back to, so no button that only looks like a label */}
        {hasPrev && (
          <Button size="sm" onClick={onFirst}>
            {firstLabel}
          </Button>
        )}
        <Button size="sm" icon="back" disabled={!hasPrev} onClick={onPrev}>
          Sebelumnya
        </Button>
        <Button size="sm" disabled={!hasNext} onClick={onNext}>
          Berikutnya
          <Icon name="next" size={16} />
        </Button>
      </div>
    </nav>
  );
}
